<template>
  <HomeLuxuryPage />
</template>

<script setup lang="ts">
import { SITE_LOCALES, localizePath } from '~/utils/locales'

definePageMeta({
  layout: 'editorial-home'
})

const currentLocale = useSiteLocale()
const homeSocialImage = 'https://kendrick.paris/social/kendrick-home-og-ankle-boot-packshot.jpg'
const canonicalUrl = computed(() => `https://kendrick.paris${localizePath('/', currentLocale.value) === '/' ? '' : localizePath('/', currentLocale.value)}`)
const seoCopy = computed(() => {
  switch (currentLocale.value) {
    case 'fr':
      return {
        title: 'Bottines et escarpins de créateur, made in Italy',
        description: 'Bottines, escarpins et sandales de créateur par Kendrick Paris. Découvrez les ALIGN 110, AXIS 110, EDGE 110 et POISE 110, conçus à Paris, made in Italy.',
        keywords: 'bottine créateur, bottines made in Italy, escarpins créateur Paris, botte créateur, sandale créateur, chaussures à talon créateur, Kendrick Paris',
        imageAlt: 'Kendrick Paris aperçu social des bottines ALIGN 110'
      }
    case 'es':
      return {
        title: 'Tacones y botines franceses, hechos en Italia',
        description: 'Botines y zapatos de tacón de diseñador por Kendrick Paris. Descubre ALIGN 110, AXIS 110, EDGE 110 y POISE 110, diseñados en París y fabricados en Italia.',
        keywords: 'tacones franceses, zapatos de diseñador, botines, botas, pumps, sandalias, Kendrick Paris',
        imageAlt: 'Vista social de los botines ALIGN 110 de Kendrick Paris'
      }
    case 'pt':
      return {
        title: 'Saltos e botins franceses, feitos na Itália',
        description: 'Botins e sapatos de salto de designer por Kendrick Paris. Descubra ALIGN 110, AXIS 110, EDGE 110 e POISE 110, desenhados em Paris e feitos na Itália.',
        keywords: 'saltos franceses, sapatos de designer, botins, botas, scarpins, sandálias, Kendrick Paris',
        imageAlt: 'Prévia social dos botins ALIGN 110 Kendrick Paris'
      }
    case 'ja':
      return {
        title: 'フレンチヒールとブーツ、イタリア製',
        description: 'Kendrick Parisのデザイナーヒールブーツとシューズ。パリでデザインされイタリアで作られるALIGN 110、AXIS 110、EDGE 110、POISE 110をご覧ください。',
        keywords: 'フレンチヒール, デザイナーシューズ, アンクルブーツ, ブーツ, パンプス, サンダル, Kendrick Paris',
        imageAlt: 'Kendrick Paris ALIGN 110 アンクルブーツのソーシャルプレビュー'
      }
    case 'ko':
      return {
        title: '프렌치 힐과 부츠, 이탈리아 제작',
        description: 'Kendrick Paris의 디자이너 힐 부츠와 슈즈. 파리에서 디자인하고 이탈리아에서 제작한 ALIGN 110, AXIS 110, EDGE 110, POISE 110을 만나보세요.',
        keywords: '프렌치 힐, 디자이너 슈즈, 앵클부츠, 부츠, 펌프스, 샌들, Kendrick Paris',
        imageAlt: 'Kendrick Paris ALIGN 110 앵클부츠 소셜 프리뷰'
      }
    case 'de':
      return {
        title: 'Französische Heels & Boots, made in Italy',
        description: 'Designer-Heels und Boots von Kendrick Paris. Entdecken Sie ALIGN 110, AXIS 110, EDGE 110 und POISE 110, entworfen in Paris und gefertigt in Italien.',
        keywords: 'französische Heels, Designer-Schuhe, Ankle Boots, Stiefel, Pumps, Sandalen, Kendrick Paris',
        imageAlt: 'Kendrick Paris ALIGN 110 Ankle Boots Social Preview'
      }
    case 'it':
      return {
        title: 'Tacchi e stivali francesi, made in Italy',
        description: 'Stivali e scarpe con tacco di design firmati Kendrick Paris. Scopri ALIGN 110, AXIS 110, EDGE 110 e POISE 110, disegnati a Parigi e realizzati in Italia.',
        keywords: 'tacchi francesi, scarpe di design, tronchetti, stivali, décolleté, sandali, Kendrick Paris',
        imageAlt: 'Anteprima social dei tronchetti ALIGN 110 Kendrick Paris'
      }
    case 'zh':
      return {
        title: '法式高跟鞋与靴履，意大利制造',
        description: 'Kendrick Paris 设计师高跟靴履与鞋款。探索在巴黎设计、意大利制作的 ALIGN 110、AXIS 110、EDGE 110 与 POISE 110。',
        keywords: '法式高跟鞋, 设计师鞋履, 短靴, 长靴, 高跟鞋, 凉鞋, Kendrick Paris',
        imageAlt: 'Kendrick Paris ALIGN 110 短靴社交预览'
      }
    default:
      return {
        title: 'French Heels & Designer Boots Made in Italy',
        description: 'French high heels, ankle boots, pumps and sandals by Kendrick Paris. Designed in Paris, made in Italy, built for posture and all-day walking comfort.',
        keywords: 'French heels, French high heels, French designer heels, French designer shoes, French designer boots, Italian made heels, ankle boots, pumps, sandals, Kendrick Paris',
        imageAlt: 'Kendrick Paris ALIGN 110 ankle boots social preview'
      }
  }
})
const pageTitle = computed(() => (
  seoCopy.value.title
))
const pageDescription = computed(() => (
  seoCopy.value.description
))
const pageKeywords = computed(() => (
  seoCopy.value.keywords
))
const socialImageAlt = computed(() => (
  seoCopy.value.imageAlt
))

useShareImage(
  '/social/kendrick-home-og-ankle-boot-packshot.jpg',
  socialImageAlt.value
)

useHead(() => ({
  title: pageTitle.value,
  meta: [
    { name: 'description', content: pageDescription.value },
    { name: 'keywords', content: pageKeywords.value },
    { name: 'robots', content: 'index, follow' },
    { name: 'author', content: 'Kendrick Paris' },
    { property: 'og:title', content: pageTitle.value },
    { property: 'og:description', content: pageDescription.value },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: 'Kendrick Paris' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:image', content: homeSocialImage },
    { property: 'og:image:secure_url', content: homeSocialImage },
    { property: 'og:image:alt', content: socialImageAlt.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: homeSocialImage },
    { name: 'twitter:title', content: pageTitle.value },
    { name: 'twitter:description', content: pageDescription.value }
  ],
  link: [
    { rel: 'canonical', href: canonicalUrl.value },
    ...SITE_LOCALES.map(locale => ({
      rel: 'alternate',
      hreflang: locale.language,
      href: `https://kendrick.paris${localizePath('/', locale.code) === '/' ? '' : localizePath('/', locale.code)}`
    })),
    { rel: 'alternate', hreflang: 'x-default', href: 'https://kendrick.paris' }
  ]
}))
</script>

<style scoped>
/* editorial — utility-only page */
</style>
